import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import { useSeo } from '@/hooks/useSeo';
import { allArticles, allSections, getArticlesBySection } from '@/utils/content';
import ContactForm from './components/ContactForm';

const PRINCIPLES = [
  {
    icon: 'ri-compass-3-line',
    title: '网站定位',
    text: '17吃瓜网是一个轻量的吃瓜话题站点。我们把娱乐、社会、科技、体育、网红圈分成独立板块，让你按兴趣直达，而不是在一条无限下拉的时间线里翻找。',
  },
  {
    icon: 'ri-seedling-line',
    title: '发展理念',
    text: '话题会过去，好用的浏览方式会留下。我们更愿意把力气花在分区的清晰度、内容的结构感上，而不是靠标题党抢一时的点击。',
  },
  {
    icon: 'ri-shield-check-line',
    title: '内容原则',
    text: '整理优先、标明出处、不做定性。涉及具体事件时，我们只讲公开讨论的背景与看点，请以官方或当事人的公开信息为准。',
  },
];

const PROCESS = [
  { step: '01', title: '全区扫描', text: '按五个板块分别盯近期讨论集中度，筛掉纯情绪化、没有信息量的内容。' },
  { step: '02', title: '公开信息核对', text: '只采用公开渠道的表述，遇到说法不一的地方直接说明，不做单方面判断。' },
  { step: '03', title: '写成结构稿', text: '把背景、经过和看点拆成小标题，加上要点速览，方便几分钟内读完。' },
  { step: '04', title: '分区归档', text: '内容按板块与标签归档，后续查找时可以直接定位到对应分区。' },
];

const STATS = [
  { value: `${allArticles.length}+`, label: '整理篇目' },
  { value: `${allSections.length}`, label: '内容板块' },
  { value: '每日', label: '更新节奏' },
  { value: '移动端', label: '优先适配' },
];

export default function AboutPage() {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';

  const jsonLd = useMemo(
    () => [
      {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: '关于 17吃瓜网',
        url: `${origin}/about`,
        inLanguage: 'zh-CN',
        description:
          '17吃瓜网的品牌介绍、网站定位、发展理念与内容原则，以及五个板块的划分说明与联系方式。',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: '17吃瓜网',
        url: `${origin}/`,
        email: 'hello@17chigua.example',
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'customer support',
          email: 'hello@17chigua.example',
          availableLanguage: ['zh-CN'],
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: `${origin}/` },
          { '@type': 'ListItem', position: 2, name: '关于我们', item: `${origin}/about` },
        ],
      },
    ],
    [origin],
  );

  useSeo({
    title: '关于我们 - 17吃瓜网',
    description:
      '了解 17吃瓜网的品牌定位、发展理念与内容原则，查看娱乐区、社会区、科技圈、体育圈、网红圈五个板块的划分说明，以及联系我们的方式。',
    keywords: '17吃瓜网,关于我们,内容原则,板块划分,联系方式',
    path: '/about',
    jsonLd,
  });

  return (
    <SiteLayout>
      <section className="w-full border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full px-4 py-8 md:px-6 md:py-10">
          <Breadcrumbs items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />
          <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-7">
              <span className="text-xs font-medium tracking-[0.25em] text-primary-600">关于我们</span>
              <h1 className="mt-2 font-heading text-3xl font-black leading-tight text-foreground-950 md:text-4xl">
                一个把吃瓜做得清爽一点的话题站点
              </h1>
              <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
                17吃瓜网从「分板块整理」这件事开始。我们观察到，很多人并不是不想看热点，而是懒得在混乱的信息流里一层层翻。
                于是我们按娱乐、社会、科技、体育、网红圈做分区，点进去就是那一堆的最近更新。
              </p>
            </div>
            <div className="lg:col-span-5">
              <div className="grid grid-cols-2 gap-3">
                {STATS.map((stat) => (
                  <div key={stat.label} className="rounded-lg border border-background-200 bg-background-50 p-4">
                    <span className="block font-heading text-2xl font-black text-primary-600">{stat.value}</span>
                    <span className="mt-1 block text-xs text-foreground-500">{stat.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full bg-background-50">
        <div className="mx-auto w-full px-4 py-14 md:px-6 md:py-16">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            {PRINCIPLES.map((item) => (
              <div key={item.title} className="rounded-lg border border-background-200 bg-background-100 p-6">
                <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-secondary-100 text-secondary-800">
                  <i className={`${item.icon} text-2xl`} />
                </span>
                <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">{item.title}</h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full border-y border-background-200 bg-background-100">
        <div className="mx-auto w-full px-4 py-14 md:px-6 md:py-16">
          <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div>
              <span className="text-xs font-medium tracking-[0.25em] text-secondary-700">板块划分</span>
              <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-3xl">
                五个板块，各自的范围
              </h2>
            </div>
            <p className="max-w-md text-sm leading-relaxed text-foreground-600">
              每个板块的边界写得很明确，方便你判断某个话题应该去哪里找。
            </p>
          </div>

          <div className="mt-8 overflow-hidden rounded-lg border border-background-200 bg-background-50">
            {allSections.map((section, index) => (
              <Link
                key={section.slug}
                to={`/section/${section.slug}`}
                className={`group flex cursor-pointer flex-col gap-3 p-5 transition-colors hover:bg-primary-50 md:flex-row md:items-center md:gap-6 md:p-6 ${
                  index !== 0 ? 'border-t border-background-200' : ''
                }`}
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                  <i className={`${section.icon} text-xl`} />
                </span>
                <span className="md:w-40 md:shrink-0">
                  <span className="block font-heading text-lg font-bold text-foreground-950">{section.name}</span>
                  <span className="block text-xs text-foreground-500">{section.tagline}</span>
                </span>
                <span className="flex-1 text-sm leading-relaxed text-foreground-600">{section.description}</span>
                <span className="flex items-center gap-2 text-xs text-foreground-500">
                  {getArticlesBySection(section.slug).length} 篇整理
                  <i className="ri-arrow-right-line text-primary-600 transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full bg-background-50">
        <div className="mx-auto w-full px-4 py-14 md:px-6 md:py-16">
          <span className="text-xs font-medium tracking-[0.25em] text-accent-700">内容流程</span>
          <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-3xl">
            一篇内容是怎么写出来的
          </h2>
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {PROCESS.map((item) => (
              <div key={item.step} className="relative rounded-lg border border-background-200 bg-background-100 p-5">
                <span className="font-heading text-3xl font-black text-accent-300">{item.step}</span>
                <h3 className="mt-2 font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="contact" className="w-full scroll-mt-20 border-y border-background-200 bg-background-100">
        <div className="mx-auto grid w-full grid-cols-1 gap-8 px-4 py-14 md:px-6 md:py-16 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-5">
            <span className="text-xs font-medium tracking-[0.25em] text-primary-600">联系方式</span>
            <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-3xl">
              想聊点什么都行
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600">
              话题推荐、板块建议、内容合作，或者只是想说说你觉得哪里还可以更好，都欢迎联系我们。
            </p>

            <ul className="mt-8 space-y-4">
              <li className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                  <i className="ri-mail-line text-lg" />
                </span>
                <span>
                  <span className="block text-sm font-medium text-foreground-950">邮箱</span>
                  <span className="block text-sm text-foreground-600">hello@17chigua.example</span>
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-secondary-100 text-secondary-800">
                  <i className="ri-time-line text-lg" />
                </span>
                <span>
                  <span className="block text-sm font-medium text-foreground-950">回复时间</span>
                  <span className="block text-sm text-foreground-600">工作日 10:00 - 18:00，通常 1-2 个工作日内回复</span>
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent-100 text-accent-800">
                  <i className="ri-question-line text-lg" />
                </span>
                <span>
                  <span className="block text-sm font-medium text-foreground-950">内容纠错</span>
                  <span className="block text-sm text-foreground-600">如果发现表述有误，请附上页面链接，我们会尽快核对修正</span>
                </span>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-7">
            <ContactForm />
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}